import React, { useCallback, useLayoutEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { useDropzone } from 'react-dropzone';
import {
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  InputAdornment,
  Typography,
} from '@material-ui/core';

import isURL from 'validator/lib/isURL';
import { formatFileSize } from 'src/utils/format';
import { WEBSITE_CHANNEL } from 'src/data/channel';
import { isEmailCsvResourceDocument } from 'src/data/performance';
import { Button } from 'src/components/Button';
import { CustomInput } from 'src/components/CustomInput';
import { TruncatedTypography } from 'src/components/TruncatedTypography';
import { EarthIcon } from 'src/assets/icons/Earth';
import { ResourceSelect } from './components/ResourceSelect';
import { useViewState } from './useViewState';
import { useStyles } from './useStyles';

export const AddResourcePopup = ({ open, onSubmit, onClose, csvResource, showWebsitesOnly }) => {
  const styles = useStyles();

  const fileInputRef = useRef();

  const [url, setUrl] = useState('');
  const [error, setError] = useState('');
  const [channel, setChannel] = useState(csvResource || WEBSITE_CHANNEL.homepage);
  const [csvFile, setCsvFile] = useState(null);
  const [isFileInputRefVisible, setIsFileInputRefVisible] = useState();

  const { creatingResource } = useViewState();

  const handleConfirm = useCallback(() => {
    if (url && !isURL(url)) {
      setError('Please enter a valid URL');
      return;
    }
    if (!!error) {
      return;
    }
    onSubmit({
      urls: [url],
      channel,
      csvFile,
    });
  }, [channel, url, error, onSubmit, csvFile]);

  const handleUrlChange = useCallback((event) => {
    setError('');
    setUrl(event.target.value);
  }, []);

  const handleTypeChange = useCallback((event) => {
    if (event.target.value) {
      setChannel(event.target.value);
    }
  }, []);

  const handleCsvItemClick = useCallback(() => {
    fileInputRef.current?.click();
  }, [fileInputRef]);

  const handleFileChange = useCallback(
    (acceptedFiles) => {
      const file = acceptedFiles[0];

      if (!file) {
        return;
      }

      setCsvFile(file);
    },
    [setCsvFile]
  );

  const isCsvChannel = isEmailCsvResourceDocument(channel);

  useLayoutEffect(() => {
    if (csvResource && isFileInputRefVisible) {
      fileInputRef.current?.click();
    }
  }, [csvResource, fileInputRef, isFileInputRefVisible]);

  const { getInputProps } = useDropzone({
    noClick: true,
    noKeyboard: true,
    accept: {
      'text/csv': ['.csv'],
    },
    onDrop: handleFileChange,
    maxFiles: 1,
  });

  return (
    <Dialog
      open={open}
      classes={{ paper: styles.root }}
      onClose={creatingResource ? null : onClose}
      maxWidth="md"
      data-testid="AddResourcePopup"
    >
      <DialogTitle className={styles.dialogTitle}>
        <Typography className={styles.title}>Add Resource</Typography>
      </DialogTitle>
      <DialogContent className={styles.dialogContent}>
        <ResourceSelect
          className={styles.selectChannel}
          value={channel}
          onChange={handleTypeChange}
          onCsvItemClick={handleCsvItemClick}
          csvUploadMode={!!csvResource}
          websitesOnly={showWebsitesOnly}
          csvFile={!!csvFile}
        />

        <input
          {...getInputProps()}
          ref={(el) => {
            fileInputRef.current = el;
            setIsFileInputRefVisible(!!el);
          }}
          style={{ display: 'none' }}
        />
        <Typography className={styles.label}>
          {isCsvChannel ? 'Selected file' : 'Enter URL'}
        </Typography>
        <div className={styles.urlWrapper}>
          {isCsvChannel ? (
            <div className={styles.fileNameWrapper}>
              {csvFile ? (
                <>
                  <TruncatedTypography className={styles.fileName}>
                    {csvFile?.name}
                  </TruncatedTypography>
                  <Typography className={styles.fileSize}>({formatFileSize(csvFile)})</Typography>
                  <Typography
                    className={styles.replace}
                    onClick={handleCsvItemClick}
                  >
                    Replace
                  </Typography>
                </>
              ) : (
                <Typography
                  className={styles.replace}
                  onClick={handleCsvItemClick}
                >
                  Add
                </Typography>
              )}
            </div>
          ) : (
            <CustomInput
              className={styles.url}
              value={url}
              onChange={(event) => handleUrlChange(event)}
              placeholder={'https://www.anyword.com/'}
              dataTestId="resourceURLInput"
              startAdornment={
                isCsvChannel ? null : (
                  <InputAdornment position="start">
                    <EarthIcon className={styles.earthIcon} />
                  </InputAdornment>
                )
              }
              error={!!error}
              autoFocus
            />
          )}
        </div>
      </DialogContent>
      <DialogActions className={styles.dialogActions}>
        <Button
          className={styles.buttonCancel}
          disabled={creatingResource}
          onClick={onClose}
          variant="text"
          color="primary"
          aria-label="Cancel"
          data-testid="AddResourcePopupCancelButton"
        >
          Cancel
        </Button>
        <Button
          className={styles.buttonConfirm}
          onClick={handleConfirm}
          disabled={(!url && !isCsvChannel) || (isCsvChannel && !csvFile)}
          loading={creatingResource}
          variant="contained"
          color="primary"
          aria-label="Confirm"
          data-testid="AddResourcePopupCTAButton"
        >
          Add resource
        </Button>
      </DialogActions>
    </Dialog>
  );
};

AddResourcePopup.propTypes = {
  className: PropTypes.string,
  open: PropTypes.bool,
  showWebsitesOnly: PropTypes.bool,
  onClose: PropTypes.func.isRequired,
};
AddResourcePopup.defaultProps = {
  className: null,
  open: false,
  showWebsitesOnly: false,
};
